import {
  camelCase,
  get,
  includes,
  isEmpty,
  isNil,
  isNull,
  join,
  kebabCase,
  map,
  replace,
  startCase,
} from 'lodash';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { searchTypes } from '../../common/helpers/ui/searchTypes';
import { handleImageUrl } from '../../common/helpers/sharpie/imageUrlHelper';
import {
  getPostCategoryInThai,
  PostSubcategory,
} from '../../common/helpers/domain/postCategories';
import { gotoSrp } from '../../common/helpers/routing/srpRoutesHelper';
import {
  getContentPagePath,
  getProjectPagePath,
} from '../../common/helpers/routing/urlGenerator';
import UrlHelper from '../../common/helpers/routing/UrlHelper';
import Icon from '../../common/styles/Icon';
import commonResource from '../../locales/th/common.json';
import LazyImage from '../lazyImage/LazyImage';
import { PlayIcon } from '../playIcon/PlayIcon';
import {
  Address,
  Author,
  CardCategory,
  CardContent,
  CardDescription,
  CardImage,
  CardTitle,
  CardWrapper,
  CommentCount,
  PriceLevel,
  ProjectInfoWrapper,
  ProjectPostingWrapper,
} from './Card.style';
import NoImagePlaceHolder from './component/NoImagePlaceHolder';
import { ProjectAttribute } from './component/ProjectAttribute';
import {
  articleBlockImageConfig,
  imageConfig,
  projectImageConfig,
} from './config/index';
import { isVideoPost, VideoType } from '../../common/helpers/domain/videoTypes';
import { WholeState } from '../../store/common/type';

const DEFAULT_TITLE_HEIGHT = 22;

interface RoomType {
  name: string;
}
interface CardProps {
  isOnLatestSection?: boolean;
  postCategory?: PostSubcategory;
  link?: string;
  title?: string;
  id?: number;
  propertyType?: string[];
  isOnSearchPage?: boolean;
  lazyLoad?: boolean;
  author?: { name: string; nickname: string };
  totalComments?: number;
  isOnArticleBlock?: boolean;
  trendingCard?: boolean;
  projectCard?: boolean;
  isOnProjectPage?: boolean;
  smallCard?: boolean;
  className?: string;
  excerpt?: string;
  videoType?: VideoType;
  clickOnCardTrack?: () => void;
  projectRelatedPosts?: { name: string; total: number }[];
  featuredImage?: { url?: string };
  roomTypes?: RoomType[];
  priceTypes?: { level?: { label?: string } };
  location?: { zone?: { label: string }[] };
}

const Card: React.FC<CardProps> = ({
  isOnLatestSection,
  postCategory,
  link = '',
  title = '',
  id,
  propertyType = [],
  isOnSearchPage,
  lazyLoad = true,
  author,
  totalComments,
  isOnArticleBlock,
  trendingCard,
  projectCard,
  isOnProjectPage,
  smallCard,
  className,
  excerpt = '',
  videoType,
  clickOnCardTrack,
  projectRelatedPosts = [],
  featuredImage = { url: '' },
  roomTypes = [],
  priceTypes = { level: { label: '' } },
  location = { zone: [] },
}) => {
  const [lineClamp, setLineClamp] = useState(2);
  const [height, setHeight] = useState('40px');

  const deviceType = useSelector((state: WholeState) =>
    state.common ? state.common.deviceType : 'desktop',
  );
  const isNextGenSupported = useSelector((state: WholeState) =>
    state.common ? state.common.isNextGenSupported : false,
  );

  const router = useRouter();

  useEffect(() => {
    const title: HTMLDivElement | null = null;
    const titleHeight = !isNil(title) && title.clientHeight;

    if (titleHeight <= DEFAULT_TITLE_HEIGHT) {
      setLineClamp(3);
      setHeight('60px');
    }
  }, [title]);

  const isProject = !!(projectCard || isOnSearchPage);
  const hrefLink = isProject
    ? getProjectPagePath(link)
    : getContentPagePath({
        title,
        propertyType,
        id,
        postCategory,
        link,
      });

  const renderCardImage = () => {
    const imageInfo = isProject
      ? projectImageConfig[deviceType]
      : isOnArticleBlock
        ? articleBlockImageConfig[deviceType]
        : imageConfig[deviceType];

    const sharpieImgUri = handleImageUrl(
      featuredImage?.url || '',
      imageInfo.width,
      imageInfo.height,
      isNextGenSupported,
    );

    const cardImage = featuredImage?.url ? (
      <LazyImage src={sharpieImgUri} alt={title} lazyLoad={lazyLoad} />
    ) : (
      <NoImagePlaceHolder />
    );

    return (
      <CardImage
        isOnLatestSection={isOnLatestSection}
        trendingCard={trendingCard}
        projectCard={projectCard}
        smallCard={smallCard}
      >
        <a href={hrefLink} onClick={clickOnCardTrack}>
          {isVideoPost(videoType) ? (
            <div className="img-wrapper">
              <span className="play-icon-wrapper">
                <PlayIcon />
              </span>
              {cardImage}
            </div>
          ) : (
            cardImage
          )}
        </a>
      </CardImage>
    );
  };

  const renderPostAuthorAndComment = () => {
    const by = commonResource.by;
    const authorLink = UrlHelper.withLink(
      '/AuthorPage/' + kebabCase(author.nickname),
      '/author/' + kebabCase(author.nickname) + '/',
    );

    return (
      !isOnArticleBlock && (
        <div>
          <Author>
            <p>
              {by} {authorLink(<a>{author.name}</a>)}
            </p>
          </Author>
          <CommentCount>
            <p>
              <Icon iconName="comments" leftOfText />
              <span>{totalComments}</span>
            </p>
          </CommentCount>
        </div>
      )
    );
  };

  const getZones = (zone: { label: string }[]) =>
    zone.map((item, index) => (
      <span key={item.label}>
        {!!index && <span>, </span>}
        <a
          onClick={() => gotoSrp({ type: searchTypes.zone, slug: item.label })}
        >
          {item.label}
        </a>
      </span>
    ));

  const renderProjectInfo = () => (
    <ProjectInfoWrapper>
      <Address>
        <span>{join(propertyType, ',')}</span>
        {!isEmpty(location.zone) && (
          <span>
            {commonResource.card.in} {getZones(location.zone)}
          </span>
        )}
      </Address>
      {!isEmpty(roomTypes) ? (
        <ProjectAttribute roomTypes={roomTypes} />
      ) : (
        <div className="roomTypePlaceholder" />
      )}
      {!isNull(priceTypes.level) && !isEmpty(get(priceTypes.level, 'label')) ? (
        <PriceLevel>
          <a
            onClick={() =>
              router.push(`/category/${get(priceTypes.level, 'label')}`)
            }
          >
            {get(priceTypes.level, 'label')}
          </a>
        </PriceLevel>
      ) : (
        <div className="priceLevelPlaceHolder" />
      )}
    </ProjectInfoWrapper>
  );

  const renderCardContent = () => {
    const categoryTranslate = ['article'];
    const description = replace(excerpt, /(<([^>]+)>)/gi, '');
    return (
      <CardContent
        trendingCard={trendingCard}
        projectCard={projectCard}
        className={className}
        smallCard={smallCard}
      >
        <CardCategory smallCard={smallCard}>
          <p data-test={'card-category'}>
            {!projectCard
              ? getPostCategoryInThai(postCategory)
              : includes(categoryTranslate, camelCase(postCategory))
                ? (commonResource.card as Record<string, string>)[
                    camelCase(postCategory)
                  ]
                : 'project'}
          </p>
        </CardCategory>

        <div
          data-test={'card-title'}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ref={(node: any) => {
            title = node;
          }}
        >
          <CardTitle
            projectCard={projectCard}
            isOnSearchPage={isOnSearchPage}
            smallCard={smallCard}
          >
            {
              <a
                href={hrefLink}
                onClick={clickOnCardTrack}
                data-test={'card-link'}
              >
                {title}
              </a>
            }
          </CardTitle>
        </div>

        {projectCard ? renderProjectInfo() : renderPostAuthorAndComment()}
        {projectCard && (
          <ProjectPostingWrapper>
            {map(projectRelatedPosts, (projectRelatedPost, index) => {
              return (
                <div key={index} className="post-item">
                  {projectRelatedPost.total > 0 ? (
                    <a href={`${hrefLink}#project-read-more`}>{`${startCase(
                      projectRelatedPost.name,
                    )} (${projectRelatedPost.total})`}</a>
                  ) : (
                    <a href={`${hrefLink}#project-read-more`}>{`${startCase(
                      projectRelatedPost.name,
                    )}`}</a>
                  )}
                </div>
              );
            })}
          </ProjectPostingWrapper>
        )}
        {smallCard && (
          <CardDescription
            smallCard={smallCard}
            lineClamp={lineClamp}
            height={height}
            dangerouslySetInnerHTML={{ __html: description }}
          />
        )}
      </CardContent>
    );
  };

  return (
    <CardWrapper
      className="card"
      data-test={'card'}
      isOnProjectPage={isOnProjectPage}
      trendingCard={trendingCard}
      projectCard={projectCard}
      smallCard={smallCard}
    >
      {renderCardImage()}
      {renderCardContent()}
    </CardWrapper>
  );
};

export default Card;
